import React, { useState, useEffect } from 'react';
import { mobileObjectDetailService } from '../services/mobileObjectDetailService';
import { useApiError } from '../../shared/hooks/useApiError';
import { MessageDialog } from '../../shared/ui';

const ImageDeleteModal = ({ 
  isOpen, 
  onClose, 
  objectId, 
  images, 
  imageData, 
  onImageDeleted 
}) => {
  // APIエラーハンドリングフック
  const { 
    showErrorDialog,
    errorMessage,
    handleApiError,
    closeErrorDialog
  } = useApiError(() => setIsLoading(false));
  
  // UI状態
  const [isLoading, setIsLoading] = useState(false); // ローディング状態
  const [selectedImages, setSelectedImages] = useState(new Set()); // 選択された画像のインデックス

  // モーダル初期化処理
  useEffect(() => {
    if (isOpen) {
      setSelectedImages(new Set());
    }
  }, [isOpen]);

  // 画像選択状態切り替え処理
  const handleImageToggle = (imageIndex) => {
    setSelectedImages(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(imageIndex)) {
        newSelected.delete(imageIndex);
      } else {
        newSelected.add(imageIndex);
      }
      return newSelected;
    });
  };

  // 画像削除処理
  const handleDeleteImages = async () => {
    if (selectedImages.size === 0) {
      MessageDialog.show('エラー', '削除する画像を選択してください。', null, 'error');
      return;
    }

    await MessageDialog.showConfirm('確認', `選択した${selectedImages.size}枚の画像を削除してもよろしいですか？`, async () => {
      try {
        setIsLoading(true);

        const sortedIndices = Array.from(selectedImages).sort((a, b) => b - a);
        const deletedIndices = [];
        const failedIndices = [];

        for (const imageIndex of sortedIndices) {
          try {
            const imageDataItem = imageData[imageIndex];
            if (imageDataItem === null || imageDataItem.objectImageId === null
              || imageDataItem.objectImageId === undefined || imageDataItem.objectImageId === '' ) {
              failedIndices.push(imageIndex);
              continue;
            }

            const result = await mobileObjectDetailService.deleteImage(objectId, imageDataItem.objectImageId);

            if (result.returnCode !== '200') {
              failedIndices.push(imageIndex);
              continue;
            }

            deletedIndices.push(imageIndex);
          } catch (error) {
            failedIndices.push(imageIndex);
          }
        }

        if (failedIndices.length > 0) {
          MessageDialog.show('エラー', `${failedIndices.length}枚の画像の削除に失敗しました。`, null, 'error');
        }

        if (onImageDeleted && deletedIndices.length > 0) {
          onImageDeleted(deletedIndices);
        }

        if (deletedIndices.length > 0) {
          onClose();
        }

      } catch (error) {
        handleApiError(new Error(`画像の削除に失敗しました。`));
      } finally {
        setIsLoading(false);
      }
    });
  };

  if (!isOpen) return null;

  const styles = {
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 3000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    modalContent: {
      background: 'white',
      padding: '20px',
      borderRadius: '10px',
      maxWidth: '90%',
      maxHeight: '90%',
      overflow: 'auto',
      width: '90%'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      paddingBottom: '10px',
      borderBottom: '1px solid #ddd'
    },
    title: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#333'
    },
    closeButton: {
      background: '#666',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      padding: '8px 16px',
      cursor: 'pointer',
      fontSize: '14px'
    },
    imageList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '5px',
      marginBottom: '20px',
      justifyContent: 'center',
      alignItems: 'center'
    },
    imageContainer: {
      position: 'relative',
      display: 'inline-block'
    },
    checkbox: {
      position: 'absolute',
      top: '5px',
      left: '5px',
      width: '20px',
      height: '20px',
      cursor: 'pointer',
      zIndex: 1
    },
    imageThumbnail: {
      width: '85px',
      height: '85px',
      objectFit: 'contain',
      borderRadius: '5px',
      backgroundColor: '#2B2222',
      display: 'block'
    },
    selectAllButton: {
      background: '#f0f0f0',
      color: '#333',
      border: '1px solid #ccc',
      borderRadius: '5px',
      padding: '8px 16px',
      cursor: 'pointer',
      fontSize: '14px',
      marginBottom: '15px'
    },
    deleteButton: {
      width: '100%',
      background: '#dc3545',
      color: 'white',
      border: 'none',
      borderRadius: '5px',
      padding: '12px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold'
    },
    deleteButtonDisabled: {
      width: '100%',
      background: '#ccc',
      color: '#666',
      border: 'none',
      borderRadius: '5px',
      padding: '12px',
      cursor: 'not-allowed',
      fontSize: '16px',
      fontWeight: 'bold'
    },
    loadingText: {
      textAlign: 'center',
      color: '#666',
      fontSize: '14px',
      margin: '20px 0'
    }
  };

  return (
    <div style={styles.modal}>
      <div style={styles.modalContent}>
        <div style={styles.header}>
          <div style={styles.title}>画像削除</div>
          <button style={styles.closeButton} onClick={onClose}>
            戻る
          </button>
        </div>

        {isLoading ? (
          <div style={styles.loadingText}>画像を削除しています...</div>
        ) : (
          <>
            <div style={styles.imageList}>
              {images && images.length > 0 ? (
                images.map((image, index) => (
                  <div key={index} style={styles.imageContainer}>
                    <input
                      type="checkbox"
                      checked={selectedImages.has(index)}
                      onChange={() => handleImageToggle(index)}
                      style={styles.checkbox}
                    />
                    <img 
                      src={image} 
                      alt={`画像 ${index + 1}`}
                      style={styles.imageThumbnail}
                      onError={(e) => {
                        e.target.src = '/core/img/errorimage.png';
                      }}
                    />
                  </div>
                ))
              ) : (
                <div style={styles.loadingText}>画像がありません</div>
              )}
            </div>

            <button 
              style={selectedImages.size > 0 ? styles.deleteButton : styles.deleteButtonDisabled}
              onClick={handleDeleteImages}
              disabled={selectedImages.size === 0 || isLoading}
            >
              画像削除
            </button>
          </>
        )}
      </div>

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title="メッセージ"
        message={errorMessage}
        type="error"
      />
    </div>
  );
};

export default ImageDeleteModal;

